// src/components/recruiter/RecruiterProfile.tsx
import React from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Progress,
  Row,
  Select,
  Skeleton,
  Typography,
  Upload,
  message,
} from "antd";
import RegisterRoleSvg from "../../../assets/register.svg";
import { UploadOutlined, ClockCircleOutlined } from "@ant-design/icons";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import ErrorPage from "components/ui/ErrorPage";
import { useAddressHelper } from "utils/useAddressHelper";
import { useUploadFiles } from "utils/media";
import { FileTypes } from "enum/file-type";
import { isJSONString } from "utils/validate-json";
import { useUpdateReacruiterProfile } from "api/recruiter/Profile/useUpdateProfile";
import { useGetRecruiterProfileInfo } from "api/recruiter/Profile/useGetProfile";
import { phoneValidator } from "utils/phone-validator";
import { numberValidator } from "utils/number-validator";
import { RoleTypes } from "enum/role-type-enum";

const { Title } = Typography;
const { Option } = Select;

const RecruiterProfile: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const updateProfileMutation = useUpdateReacruiterProfile();
  const { data, error, isPending } = useGetRecruiterProfileInfo(id);

  const {
    address,
    setAddress,
    getProvinceOptions,
    getDistrictOptions,
  } = useAddressHelper();

  const {
    uploadImage,
    onChange,
    beforeUpload,
    normFile,
    getFileLink,
    cvID,
    setCVId,
    uploading,
  } = useUploadFiles();

  React.useEffect(() => {
    if (!data) return;
    (async () => {
      if (data.companyLogo?._id) {
        setCVId(data.companyLogo._id);
        const file = await getFileLink(data.companyLogo._id);
        form.setFieldsValue({ companyLogo: file });
      } else {
        form.setFieldsValue({ companyLogo: [] });
      }
    })();
  }, [data, form, getFileLink, setCVId]);

  React.useEffect(() => {
    if (!data) return;
    form.setFieldsValue({
      companyName: data.companyName,
      userName: data.userName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      noOfEmployees: data.noOfEmployees,
      registrationNumber: data.registrationNumber,
      address: data.address,
    });
  }, [data, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      companyLogo: cvID,
      address: {
        ...values.address,
        state: isJSONString(values.address.state)
          ? JSON.parse(values.address.state).name
          : values.address.state,
      },
      role: RoleTypes.RECRUITER,
    };

    if (id) {
      updateProfileMutation.mutate(
        { id, recruiter: payload },
        {
          onSuccess: () => {
            form.resetFields();
            navigate("/recruiter/home");
          },
        }
      );
    } else {
      message.error("User id not found");
    }
  };

  if (isPending) {
    return (
      <div className="bg-background min-h-screen">
        <div className="max-w-6xl mx-auto py-12">
          <Card style={{ width: "100%", marginTop: 16, height: "90vh" }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <div className="bg-background">
      <div className="max-w-6xl mx-auto py-12">
        <Card className="px-8 py-2">
          <div className="flex justify-between items-center">
            <Title level={2} style={{ fontWeight: 700 }}>
              Let’s set up your profile
            </Title>
            <img alt="register" src={RegisterRoleSvg} />
          </div>
        </Card>

        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Card className="px-8 py-2 mt-8">
            <Row gutter={[48, 32]}>
              {/* Company Name */}
              <Col span={24}>
                <Form.Item
                  name="companyName"
                  label="Company Name"
                  rules={[{ required: true, message: "This is required" }]}
                >
                  <Input placeholder="eg. Royal Remit Pvt Ltd" />
                </Form.Item>
              </Col>

              {/* User Name */}
              <Col span={24}>
                <Form.Item
                  name="userName"
                  label="User Name"
                  rules={[{ required: true, message: "This is required" }]}
                >
                  <Input placeholder="eg. microsoft_username" />
                </Form.Item>
              </Col>

              {/* Email */}
              <Col xs={24} lg={12}>
                <Form.Item
                  name="email"
                  label="Email address"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Please enter valid email" },
                  ]}
                >
                  <Input placeholder="eg. example@gmail.com" />
                </Form.Item>
              </Col>

              {/* No. of Employees */}
              <Col span={24}>
                <Form.Item
                  name="noOfEmployees"
                  label="Number of employees"
                  rules={[
                    { required: true, message: "This is required" },
                    { validator: numberValidator },
                  ]}
                >
                  <Input placeholder="eg. 2" />
                </Form.Item>
              </Col>

              {/* Reg. Number */}
              <Col span={24}>
                <Form.Item
                  name="registrationNumber"
                  label="Registration Number"
                  rules={[
                    { required: true, message: "This is required" },
                    { validator: numberValidator },
                  ]}
                >
                  <Input placeholder="eg. 1453773878484" />
                </Form.Item>
              </Col>

              {/* Phone */}
              <Col span={24}>
                <Form.Item
                  name="phoneNumber"
                  label="Phone Number"
                  rules={[
                    { required: true, message: "Phone number is required" },
                    { validator: phoneValidator },
                  ]}
                >
                  <Input placeholder="eg. 9848442507" />
                </Form.Item>
              </Col>

              {/* Address fields… */}
              <Col span={24}>
                <Form.Item
                  name={["address", "state"]}
                  label="Province"
                  rules={[{ required: true, message: "This is required" }]}
                >
                  <Select
                    allowClear
                    placeholder="Select province"
                    options={getProvinceOptions()}
                    onChange={(val) => setAddress({ ...address, province: val })}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name={["address", "district"]}
                  label="District"
                  rules={[{ required: true, message: "This is required" }]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select district"
                    optionFilterProp="children"
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(val) => setAddress({ ...address, district: val })}
                  >
                    {getDistrictOptions().map((d) => (
                      <Option key={d.label} value={d.label}>
                        {d.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {/* …municipality, ward (same pattern) */}

              {/* Company Logo */}
              <Col span={24}>
                <Title level={2}>Upload Company Logo</Title>
                <Form.Item
                  name="companyLogo"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  rules={[{ required: true, message: "Please upload your logo." }]}
                >
                  <Upload
                    maxCount={1}
                    onChange={onChange}
                    customRequest={(opts) =>
                      uploadImage(opts, {
                        type: FileTypes.CV,
                        service: "recruiter",
                        tags: ["recruiter", "logo"],
                        description: "logo of recruiter",
                      })
                    }
                    beforeUpload={beforeUpload}
                    listType="picture"
                    progress={{ showInfo: true }}
                  >
                    <Button icon={<UploadOutlined />}>Click to upload</Button>
                  </Upload>
                </Form.Item>
                {uploading && <Progress percent={50} />}
              </Col>

              {/* Terms checkbox */}
              <Col span={24}>
                <Form.Item name="isTermAgreed" valuePropName="checked">
                  <Checkbox>
                    By clicking this box you agree to receive marketing and informational
                    calls and texts from Hire enumerator.
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <div className="flex justify-between pt-12">
              <Button onClick={() => navigate("/recruiter/home")}>
                <ClockCircleOutlined /> Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={updateProfileMutation.isPending}>
                Save
              </Button>
            </div>
          </Card>
        </Form>
      </div>
    </div>
  );
};

export default RecruiterProfile;
